/**
 * Générateur — Attendu 193 — O3 « Il écrit un script de déplacement ou de
 * construction géométrique utilisant des instructions conditionnelles et/ou la
 * boucle « Répéter … fois ». »
 * Niveau : 4ème | Thème #12 Algorithmique (O3) | Session A.
 * Source prof : O3 (Créé n°20 « les trois polygones du référentiel » + Créé n°21
 * « première conditionnelle » : rebond au bord).
 *
 * Création (2026-07) : sept cartes, polygones réguliers de 3 à 8 côtés (trous en
 * saisie), rectangle, « pourquoi l'angle extérieur ? », lutin au bord de l'écran
 * dans une boucle « répéter indéfiniment », scripts de rebond en QCM d'images.
 *
 * Revue 2026-09-23 (vague 4 ; audit mesuré, décisions du coordinateur, mode
 * auto). Plafond : niveaux 1 et 2 des attendus de fin de 4e (Eduscol, p. 11-12).
 * Aucun solveur test-side ne couvre ce gen ; les réponses sont prouvées dans le
 * gen lui-même (tortue du quadrillage, lutin au bord, compte des conditions),
 * throw sinon. Pas de rappel brouillon : lecture de script, 360 ÷ 3 de tête.
 *
 *   - « Le script du triangle équilatéral ou du carré »
 *     (poly_regulier_texte_trou_saisie devient poly_regulier_texte_trou_drag).
 *     Sert l'exemple de réussite « Il produit seul un programme de construction
 *     d'un triangle équilatéral, d'un carré ou d'un rectangle en utilisant la
 *     boucle ». n ∈ {3 ; 4} : pentagone, hexagone et octogone sortent de cet
 *     exemple (ils restent servis en 5e par 1002), et « le hexagone »,
 *     « le octogone » (39,5 % des tirages) avec eux. La phrase « le lutin tourne
 *     de l'angle EXTÉRIEUR » sort : vocabulaire hors programme, méthode soufflée.
 *     En saisie, « tourner ↻ de 360 − 360/n » et « répéter 2n fois » traçaient
 *     aussi le polygone et étaient refusés (100 %) : la phrase passe en drag, avec
 *     les MÊMES étiquettes pour les deux polygones (3, 4, 60, 90, 120, 360),
 *     jamais 240, 270 ni 2n. La consigne dit « avec le moins de répétitions
 *     possible » : l'angle juste posé, l'étiquette 4, 60, 90 ou 360 en
 *     répétitions trace aussi le triangle, et 60, 120 ou 360 le carré, en
 *     repassant sur les côtés (tortue, 100 % des tirages) ; la consigne impose la
 *     forme au lieu de refuser un script qui marche, et le placement le plus
 *     court est alors unique. Phrase ajoutée par le correcteur, hors décisions :
 *     mode auto, À ARBITRER.
 *   - « Le script d'un rectangle » (poly_rectangle_texte_trou_saisie) : même
 *     exemple de réussite. Commencer par la largeur trace le même rectangle et
 *     était refusé (100 %) : la longueur est écrite en clair, les trous sont le
 *     nombre de répétitions et la largeur. « On répète deux fois le couple
 *     « longueur, largeur ». » sort : cette phrase donnait le premier trou.
 *     Largeur toujours plus courte que la longueur (inversées dans 40,9 %).
 *     Même phrase de forme que la carte précédente : répéter 3, 4… fois le même
 *     corps trace aussi le rectangle (trouvé à la tortue, hors audit ; mode
 *     auto, À ARBITRER).
 *   - « Quel script trace le rectangle ? » (rect_couple_qcm_unique, ancien
 *     « Le rectangle : une seule boucle simple ? », une seule réponse juste en
 *     phrase fixe, « rectangle 60 × 170 » dans 40,4 %) : QCM d'IMAGES de
 *     scripts. Sert l'exemple de réussite « Il retrouve parmi des programmes
 *     donnés celui qui permet d'obtenir une figure donnée, et inversement. »
 *     Familles et garanties dans REGLES_DISTRACTEURS_QCM ; la tortue d'algo.js
 *     prouve que la clé trace le rectangle construit directement et que chaque
 *     distracteur trace une AUTRE figure. Interdits, parce qu'ils tracent aussi
 *     le rectangle : répéter 4 fois [L, 90, l, 90], la largeur avant la
 *     longueur, des virages ↺. Les quatre choix sont dessinés dans le même cadre
 *     et ne montrent que la boucle, le stylo baissé étant dit dans l'énoncé
 *     (lisibilité, voir blocsTrace ; mode auto, À ARBITRER) ; la clé n'est
 *     jamais seule au centre des quatre scripts, à égalité avec virage_oublie
 *     une fois sur deux.
 *   - « Pourquoi l'angle extérieur ? » (angle_ext_qcm_unique) : SORTIE. Clé la
 *     plus longue et seule à porter le mot « EXTÉRIEUR » (100 %), vocabulaire
 *     hors plafond.
 *   - « La conditionnelle : que fait le lutin au bord ? » (cond_bord_qcm_unique) :
 *     virage de 180° seulement. À 90° (49,5 % des tirages), la clé « longe le
 *     bord » était probablement fausse dans Scratch : « touche le bord ? » reste
 *     vrai après le quart de tour, le lutin tourne une seconde fois et repart en
 *     sens inverse. Pas de 20 à 60 (5 et 10 risquaient l'accrochage au bord).
 *     « répéter indéfiniment » devient « répéter 50 fois », texte et figure : le
 *     plafond ne connaît que « Répéter … fois ». Figure avec alt ; « Un lutin
 *     (le chat) » devient « Le chat, un lutin, » ; « SI … ALORS » en capitales
 *     retiré. Sert « Il écrit un script de déplacement … utilisant des
 *     instructions conditionnelles » (lire avant d'écrire : « Il décrit ce que
 *     fait un assemblage simple de blocs de programmation »). Seconde passe
 *     (décision du coordinateur) : voir plus bas, l'action du « si » varie.
 *   - « La conditionnelle : et sans elle ? » (cond_sans_qcm_unique) : SORTIE.
 *     Comportement de la scène sans aucun bloc affiché, une seule réponse juste.
 *   - « La conditionnelle : quel script fait rebondir ? » (cond_script_qcm_unique) :
 *     les quatre scripts sont faits des MÊMES blocs (le distracteur « boucle
 *     dans le si » n'avait pas de bloc « tourner » et s'éliminait sans lire
 *     l'imbrication, 100 %) ; « indéfiniment » retiré ; même cadre pour les
 *     quatre images (la clé était la plus large, 100 %) ; alt sur chaque choix ;
 *     le <desc> qu'aucun solveur ne lisait est retiré ; le lutin part du centre
 *     de l'écran. Sert « Il écrit une séquence d'instructions (condition « si …
 *     alors » et boucle « répéter … fois ») » (niveau 2).
 *   - Figures d'énoncé et choix dessinés par svgScriptsMemeCadre (ajout à
 *     svgScratch) : marge haute de 8, la bosse du chapeau ne sort plus du viewBox.
 *   - NOUVELLE « La conditionnelle : combien de phrases ? » (phrases_qcm_unique),
 *     mode auto, À ARBITRER : le script de niveau 2 d'Eduscol (« Il produit des
 *     scripts du type : », répéter 20 fois, un tirage entre 0 et 1, deux
 *     « si … alors » qui font dire une phrase), dessiné avec les blocs de
 *     svgScratch. La variable s'appelle « tirage » : plus court que le
 *     « Nombre aléatoire choisi » d'Eduscol, et « mettre nombre à nombre
 *     aléatoire » se lisait mal. Blocs « dire … pendant 2 secondes » comme
 *     ailleurs dans le parc. N pair de 10 à 30. Rang de la clé : voir la
 *     seconde passe, plus bas.
 *   - Libellés de format « Saisie trous », « Drag », « QCM », « QCM images » ;
 *     export REGLES_DISTRACTEURS_QCM ; codes des pièges dans
 *     _meta.params.distracteurs. Passent de 7 formats à 6.
 *
 * Revue 2026-09-23 (vague 4, seconde passe, décisions du coordinateur) :
 *   - « La conditionnelle : que fait le lutin au bord ? » : la réponse juste
 *     était la même à chaque tirage (100 %). L'action du « si touche le bord ?
 *     alors » est tirée, un tiers chacune : « tourner ↻ de 180 degrés » (il
 *     rebondit), « stop tout » (il s'arrête au bord), « aller à x : 0 y : 0 »
 *     (il revient au centre). Les quatre phrases proposées sont les mêmes à
 *     chaque tirage : ces trois comportements, plus « Il sort de l'écran et
 *     disparaît. », jamais juste. Pièges nommés comme_<action> (le comportement
 *     d'une autre action) et sort_de_l_ecran ; « quart de tour » sort. Le lutin
 *     au bord de la simulation connaît les deux nouveaux blocs, pris parmi les
 *     blocs génériques de svgScratch (rien d'ajouté), et doit montrer le seul
 *     comportement de la clé, throw sinon.
 *   - « La conditionnelle : combien de phrases ? » : la clé N était toujours la
 *     deuxième plus grande des quatre valeurs (100 %). Une seconde forme de
 *     script, tirée une fois sur deux, dit une phrase sans condition en tête de
 *     la boucle, avant le tirage : la clé devient 2N, les pièges sont N (la
 *     phrase sans condition oubliée), N + N ÷ 2 (« une fois sur deux » pour les
 *     « si ») et 3 (les blocs « dire » écrits), et la clé y est la plus grande.
 *     3N (chaque « dire » compté à chaque tour) est écarté : c'est la seule
 *     erreur au-dessus de 2N, elle remettrait la clé au deuxième rang. Rang de
 *     la clé : deuxième une fois sur deux, premier une fois sur deux (49,5 % et
 *     50,5 % mesurés, l'aléa du tirage de la forme). Passer nettement sous la
 *     moitié demanderait une seconde erreur au-dessus de la clé dans une forme,
 *     non attestée : mode auto, À ARBITRER.
 */

import {
    randInt, randomChoice, makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import {
    svgScriptsMemeCadre, altScript, imgElement, imgChoix,
    figureTortue, rectangleCanonique,
} from './algo';

const ATTENDU_ID = 193;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_POLY = 'Le script du triangle équilatéral ou du carré';
const L_RECT = 'Le script d\'un rectangle';
const L_RECT_COUPLE = 'Quel script trace le rectangle ?';
const L_COND_BORD = 'La conditionnelle : que fait le lutin au bord ?';
const L_COND_SCRIPT = 'La conditionnelle : quel script fait rebondir ?';
const L_PHRASES = 'La conditionnelle : combien de phrases ?';

// La consigne impose la forme : un script qui répète plus de fois que
// nécessaire trace la même figure en repassant sur ses côtés.
const MOINS_DE_REPETITIONS = 'avec le moins de répétitions possible';

const NOMS = ['le chat', 'le robot', 'la fusée', 'le poisson', 'la coccinelle', 'le dragon'];
const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque piège est une erreur NOMMÉE d\'élève (codes dans _meta.params.distracteurs), jamais une option inventée à l\'assemblage',
        'la bonne réponse est prouvée par un second chemin dans le gen : tortue du quadrillage (rectangle), lutin au bord en une dimension (rebond), compte des conditions vraies (phrases) ; throw sinon',
        'les choix d\'un QCM d\'images sont dessinés dans le même cadre (svgScriptsMemeCadre) : même largeur de blocs, même viewBox, donc même taille à l\'écran ; aucun détail propre à la clé',
    ],
    poly_regulier_texte_trou_drag: {
        support: 'répéter {{n}} fois [ avancer de c pas, tourner ↻ de {{360 ÷ n}} degrés ], n ∈ {3 ; 4}, c de 40 à 180 pas',
        etiquettes: 'les mêmes six pour les deux polygones : 3, 4, 60, 90, 120, 360 ; deux sont les mots attendus, quatre sont des pièges',
        familles: {
            angle_interieur: '60 pour le triangle : l\'angle de la figure au lieu du virage du lutin (doc 1002, « le piège fondamental »)',
            trois_repetitions: '3 pour le carré : un côté de moins (doc 1002, carte « Écrire le carré avec une boucle »)',
            repetitions_du_carre: '4 pour le triangle : le nombre de répétitions du carré, le script modèle',
            angle_du_carre: '90 pour le triangle : le virage du carré, appliqué à toute figure',
            angle_du_triangle: '120 pour le carré : le virage du triangle',
            angle_interieur_du_triangle: '60 pour le carré : l\'angle du triangle équilatéral, le nombre qu\'on associe à cette figure',
            tour_complet: '360 : le tour complet mis dans chaque virage (doc 1002)',
        },
        exclues: '240 et 270 (360 − 360/n : le lutin tourne dans l\'autre sens et trace aussi le polygone) et 2n (le polygone tracé deux fois), jamais parmi les étiquettes',
        garantie: 'filtrerDistracteurs puis quatre pièges exigés ; un virage de a degrés referme la figure en 360 ÷ a côtés : seule l\'étiquette 360 ÷ n la referme en n côtés ; l\'angle juste posé, une autre étiquette de répétitions laisse la figure ouverte (moins de n) ou repasse sur ses côtés (plus de n), ce que la consigne exclut (« avec le moins de répétitions possible »)',
    },
    rect_couple_qcm_unique: {
        support: 'quatre boucles « répéter … fois [ … ] » dessinées seules (le stylo baissé est dit dans l\'énoncé), L de 80 à 200 pas, l de 40 pas à L − 20',
        cle: 'répéter 2 fois [avancer de L, tourner ↻ de 90, avancer de l, tourner ↻ de 90]',
        familles: {
            un_seul_couple: 'répéter 4 fois [avancer de L, tourner ↻ de 90] : le script du carré repris avec la longueur, un carré de côté L',
            virage_oublie: 'répéter 2 fois [avancer de L, tourner ↻ de 90, avancer de l] : le dernier virage oublié, une ligne en escalier',
            une_seule_longueur: 'répéter 2 fois [avancer de L, tourner ↻ de 90, avancer de L, tourner ↻ de 90] : la largeur oubliée, un carré de côté L',
            demi_tour: 'répéter 2 fois [avancer de L, tourner ↻ de 180, avancer de l, tourner ↻ de 180] : le virage d\'un rectangle pris pour un demi-tour, un segment',
        },
        tirage: 'un_seul_couple et virage_oublie toujours ; le troisième tiré entre une_seule_longueur et demi_tour. Avec les quatre familles au hasard, la clé serait seule au centre des quatre scripts (distance d\'édition sur les blocs) dans la moitié des tirages ; avec ce tirage, jamais (à égalité avec virage_oublie une fois sur deux)',
        interdits: 'répéter 4 fois [L, 90, l, 90], la largeur avant la longueur, des virages ↺ : ils tracent aussi le rectangle',
        garantie: 'figureTortue (algo.js) : la clé trace le rectangle L × l construit directement (rectangleCanonique), chaque distracteur trace une autre figure, à un déplacement près ; throw sinon',
    },
    cond_bord_qcm_unique: {
        support: 'quand le drapeau est cliqué, répéter 50 fois [avancer de d pas, si touche le bord ? alors [ACTION]], d de 20 à 60 ; ACTION tirée, un tiers chacune : tourner ↻ de 180 degrés, stop tout, aller à x : 0 y : 0',
        cles: {
            tourner_180: 'Il repart en sens inverse : il rebondit sur le bord.',
            stop_tout: 'Il s\'arrête définitivement au bord.',
            aller_au_centre: 'Il revient au centre de l\'écran.',
        },
        familles: {
            comme_tourner_180: 'prête à l\'action affichée le comportement de « tourner ↻ de 180 degrés » : il rebondit',
            comme_stop_tout: 'prête à l\'action affichée le comportement de « stop tout » : il s\'arrête au bord',
            comme_aller_au_centre: 'prête à l\'action affichée le comportement de « aller à x : 0 y : 0 » : il revient au centre',
            sort_de_l_ecran: 'ignore la conditionnelle et le bord de la scène : le lutin continue tout droit et disparaît ; jamais juste, Scratch retient le lutin sur la scène',
        },
        supprimees: {
            quart_de_tour: 'la phrase « il tourne d\'un quart de tour et longe le bord » décrivait l\'ancien virage de 90 degrés, retiré ; les choix sont désormais les trois comportements tirés et la sortie de l\'écran',
            bloque_au_bord: 'la même phrase, « Il s\'arrête définitivement au bord. », est devenue la clé de « stop tout » ; pour les deux autres actions elle compte sous comme_stop_tout',
        },
        garantie: 'les quatre mêmes phrases à chaque tirage, deux à deux différentes ; la clé est celle de l\'action tirée ; le script est exécuté par le lutin au bord en une dimension, un seul comportement doit apparaître et ce doit être celui de la clé (50 × d > 200 : le bord est toujours atteint) ; throw sinon',
    },
    cond_script_qcm_unique: {
        support: 'quatre scripts faits des mêmes blocs : quand le drapeau est cliqué, répéter 50 fois, avancer de d pas, si touche le bord ? alors, tourner ↻ de 180 degrés ; d de 20 à 60 ; le lutin part du centre',
        cle: 'le « si » dans la boucle, après « avancer » : le bord est testé à chaque pas',
        familles: {
            si_apres_boucle: 'le « si » après la boucle : testé une seule fois, quand le lutin a fini d\'avancer (doc 4e, Créé n°21)',
            si_avant_boucle: 'le « si » avant la boucle : testé une seule fois, au départ, loin du bord',
            si_mal_emboite: '« avancer » dans le « si » et « tourner » dans la boucle : parti du centre, le lutin ne touche jamais le bord et tourne sur place',
        },
        garantie: 'les trois familles toujours présentes, quatre choix ; chaque script est exécuté par le lutin au bord en une dimension : la clé rebondit au moins une fois, les trois autres jamais ; throw sinon',
    },
    phrases_qcm_unique: {
        support: 'script de niveau 2 d\'Eduscol : quand le drapeau est cliqué, répéter N fois [mettre tirage à nombre aléatoire entre 0 et 1, si tirage = 0 alors dire …, si tirage = 1 alors dire …], N pair de 10 à 30 ; une fois sur deux, un « dire » sans condition en tête de la boucle, avant le tirage',
        formes: {
            sans_phrase_fixe: {
                cle: 'N : à chaque tour le tirage vaut 0 ou 1, une seule des deux conditions est vraie',
                familles: {
                    deux_si_comptes: '2N : une phrase par bloc « si » à chaque tour, sans voir que les deux conditions s\'excluent',
                    moitie: 'N ÷ 2 : raisonne sur le hasard, « une fois sur deux », et ne compte qu\'un des deux « si »',
                    une_phrase_par_si: '2 : compte les blocs « dire » écrits dans le script, pas les fois où ils sont exécutés',
                },
                rang_de_la_cle: 'deuxième plus grande',
            },
            avec_phrase_fixe: {
                cle: '2N : la phrase sans condition à chaque tour, plus une seule des deux phrases conditionnelles',
                familles: {
                    phrase_sans_condition_oubliee: 'N : ne compte que les phrases des « si », la phrase dite à chaque tour est oubliée',
                    moitie: 'N + N ÷ 2 : la phrase sans condition à chaque tour, puis « une fois sur deux » pour les « si », un seul des deux compté',
                    une_phrase_par_bloc_dire: '3 : compte les blocs « dire » écrits dans le script, pas les fois où ils sont exécutés',
                },
                rang_de_la_cle: 'la plus grande',
            },
        },
        supprimees: {
            chaque_dire_compte: '3N (chaque bloc « dire » compté à chaque tour, sans voir que les deux « si » s\'excluent) : la seule erreur au-dessus de 2N ; présente, elle remettrait la clé au deuxième rang, comme dans la première forme, et le rang 2 dépasserait la moitié des tirages',
        },
        garantie: 'N pair et au moins 10 : dans chaque forme, les quatre valeurs sont des entiers deux à deux distincts (N ÷ 2 ≥ 5 > 3, N < N + N ÷ 2 < 2N) ; aucune boucle de complément ; la clé est recalculée en exécutant le corps dessiné pour les deux tirages possibles',
        residuel: 'la clé est la deuxième plus grande dans la forme sans phrase fixe et la plus grande dans l\'autre, chaque rang vaut donc la part de sa forme : la moitié des tirages, à l\'aléa près (49,5 % et 50,5 % mesurés sur 10 000). Descendre nettement sous la moitié demande un troisième rang, donc une seconde erreur au-dessus de la clé dans une forme : il n\'en existe qu\'une par forme (2N, 3N), je n\'en ai pas inventé ; À ARBITRER',
    },
};

// ═════════ Carte « poly_regulier » : triangle équilatéral ou carré (drag) ═════════

const POLYGONES = [
    { n: 3, nom: 'triangle équilatéral' },
    { n: 4, nom: 'carré' },
];

// Les mêmes six étiquettes pour les deux polygones, donc l'ensemble ne dit rien
// du polygone : 3 et 4 (répétitions), 60, 90, 120 et 360 (virages). Pour chaque
// polygone, les deux mots attendus en sont retirés et les quatre autres sont des
// pièges nommés.
const PIEGES_POLY = {
    3: { 4: 'repetitions_du_carre', 60: 'angle_interieur', 90: 'angle_du_carre', 360: 'tour_complet' },
    4: { 3: 'trois_repetitions', 60: 'angle_interieur_du_triangle', 120: 'angle_du_triangle', 360: 'tour_complet' },
};
const ANGLES_ETIQUETTES = [60, 90, 120, 360];

const genPolyRegulier = () => {
    const p = randomChoice(POLYGONES);
    const angle = 360 / p.n;
    const cote = randInt(4, 18) * 10;
    const phrase = `répéter {{${p.n}}} fois [ avancer de ${cote} pas, tourner ↻ de {{${angle}}} degrés ]`;
    const pieges = PIEGES_POLY[p.n];
    const distracteurs = filtrerDistracteurs(phrase, Object.keys(pieges));
    if (distracteurs.length !== 4) throw new Error('gen_193 : quatre étiquettes pièges attendues');
    // 360 − 360/n (virages dans l'autre sens) et 2n (deux tours) tracent aussi le
    // polygone : jamais parmi les étiquettes.
    [String(360 - angle), String(2 * p.n)].forEach((e) => {
        if (distracteurs.includes(e)) throw new Error(`gen_193 : l'étiquette ${e} tracerait aussi le polygone`);
    });
    // Second chemin : un virage constant de a degrés referme la figure en
    // 360 ÷ a côtés. La clé la referme en n côtés, aucune autre étiquette
    // d'angle ne le fait (360 ne tourne pas : une ligne droite).
    if ((360 / angle) !== p.n) throw new Error('gen_193 : le virage ne referme pas le polygone');
    ANGLES_ETIQUETTES.filter((a) => a !== angle).forEach((a) => {
        if (a < 360 && 360 % a === 0 && 360 / a === p.n) throw new Error(`gen_193 : l'étiquette ${a} referme aussi le polygone`);
    });
    const texte = `<p><b>Complète le script qui trace le ${p.nom} de côté ${cote} pas, `
        + `${MOINS_DE_REPETITIONS}.</b></p>`;
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_POLY), mode: 'drag', distracteurs });
    payload._meta.params = { ...p, angle, cote, distracteurs: distracteurs.map((e) => pieges[e]) };
    return payload;
};

// ═════════ Carte « poly_rectangle » : le rectangle (saisie, 2 trous) ═════════

const UNITE = 10;   // toutes les longueurs de rectangle sont des multiples de 10 pas

const tirerRectangle = () => {
    const L = randInt(8, 20) * 10;            // 80 à 200 pas
    const l = randInt(4, L / 10 - 2) * 10;    // 40 pas à L − 20 : la largeur est la plus courte
    return { L, l };
};

const genPolyRectangle = () => {
    const { L, l } = tirerRectangle();
    // Second chemin : la tortue trace le rectangle avec 2 répétitions, pas avec 1.
    const corps = [{ avancer: L }, { tourner: 90 }, { avancer: l }, { tourner: 90 }];
    const cible = rectangleCanonique(L / UNITE, l / UNITE);
    if (figureTortue(2, corps, UNITE) !== cible || figureTortue(1, corps, UNITE) === cible) {
        throw new Error('gen_193 : le rectangle ne demande pas exactement deux répétitions');
    }
    const phrase = `répéter {{2}} fois [ avancer de ${L} pas, tourner ↻ de 90 degrés, `
        + `avancer de {{${l}}} pas, tourner ↻ de 90 degrés ]`;
    const texte = `<p><b>Complète le script qui trace le rectangle de longueur ${L} pas et de `
        + `largeur ${l} pas, ${MOINS_DE_REPETITIONS}.</b></p>`;
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_RECT), mode: 'saisie' });
    payload._meta.params = { L, l };
    return payload;
};

// ═════════ Carte « rect_couple » : quel script trace le rectangle ? (QCM images) ═════════

const SCRIPTS_RECT = {
    cle: (L, l) => ({ n: 2, corps: [{ avancer: L }, { tourner: 90 }, { avancer: l }, { tourner: 90 }] }),
    un_seul_couple: (L) => ({ n: 4, corps: [{ avancer: L }, { tourner: 90 }] }),
    virage_oublie: (L, l) => ({ n: 2, corps: [{ avancer: L }, { tourner: 90 }, { avancer: l }] }),
    une_seule_longueur: (L) => ({ n: 2, corps: [{ avancer: L }, { tourner: 90 }, { avancer: L }, { tourner: 90 }] }),
    demi_tour: (L, l) => ({ n: 2, corps: [{ avancer: L }, { tourner: 180 }, { avancer: l }, { tourner: 180 }] }),
};

// Le choix ne montre que la boucle : avec le drapeau et le stylo en plus, le
// script fait 262 px de haut, et l'app borne une image de choix à 144 px (112 sur
// téléphone) : le texte des blocs tombait à 7 px (5,6 sur téléphone). Boucle
// seule : 194 px, texte à 9,6 px (7,5). Le stylo baissé est dit dans l'énoncé.
const blocsTrace = ({ n, corps }) => [
    { t: 'repeat', n, body: corps.map((ins) => (ins.tourner !== undefined
        ? { t: 'move', txt: `tourner ↻ de ${ins.tourner} degrés` }
        : { t: 'move', txt: `avancer de ${ins.avancer} pas` })) },
];

const genRectCouple = () => {
    const { L, l } = tirerRectangle();
    const codes = ['un_seul_couple', 'virage_oublie', randomChoice(['une_seule_longueur', 'demi_tour'])];
    const cle = SCRIPTS_RECT.cle(L, l);
    const pieges = codes.map((code) => SCRIPTS_RECT[code](L, l));
    // Preuve par la tortue : la clé trace le rectangle construit directement,
    // chaque piège trace une autre figure (à un déplacement près).
    const cible = rectangleCanonique(L / UNITE, l / UNITE);
    if (figureTortue(cle.n, cle.corps, UNITE) !== cible) throw new Error('gen_193 : la clé ne trace pas le rectangle');
    pieges.forEach((s, i) => {
        if (figureTortue(s.n, s.corps, UNITE) === cible) throw new Error(`gen_193 : ${codes[i]} trace aussi le rectangle`);
    });
    const listeBlocs = [cle, ...pieges].map(blocsTrace);
    const choix = svgScriptsMemeCadre(listeBlocs).map((svg, i) => imgChoix(svg, altScript(listeBlocs[i])));
    const texte = '<p>Le lutin a son stylo en position d\'écriture.</p>'
        + `<p><b>Lequel de ces scripts trace le rectangle de longueur ${L} pas et de `
        + `largeur ${l} pas ?</b></p>`;
    const payload = makeQcm(choix, choix[0], texte, opts(L_RECT_COUPLE));
    payload._meta.params = { L, l, distracteurs: codes };
    return payload;
};

// ═════════ Le lutin au bord de l'écran (second chemin des deux cartes suivantes) ═════════

const DRAPEAU = { t: 'hat', txt: 'quand le drapeau est cliqué' };
const TOURNER_180 = { t: 'move', txt: 'tourner ↻ de 180 degrés' };
// Blocs génériques de svgScratch (un rectangle et son texte, couleur de la
// famille) : « stop tout » est un bloc Contrôle, « aller à » un bloc Mouvement.
const STOP_TOUT = { t: 'control', txt: 'stop tout' };
const ALLER_AU_CENTRE = { t: 'move', txt: 'aller à x : 0 y : 0' };
const avancerDe = (d) => ({ t: 'move', txt: `avancer de ${d} pas` });
const siBord = (corps) => ({ t: 'if', cond: 'touche le bord ?', body: corps });
const repeter50 = (corps) => ({ t: 'repeat', n: 50, body: corps });

// Modèle en une dimension : la scène retient le lutin entre −BORD et BORD (Scratch
// l'empêche d'en sortir) et « touche le bord ? » est vrai quand il y est. Départ
// au centre, vers la droite. Ce que le lutin fait AU BORD est compté : un rebond
// (un demi-tour pris au bord, suivi d'un déplacement dans l'autre sens), un retour
// au centre (« aller à x : 0 y : 0 » exécuté au bord), un arrêt (« stop tout »
// exécuté au bord : plus rien ne s'exécute). Seuls les blocs de ces cartes sont
// connus, un autre bloc lève une erreur.
const BORD = 200;

const executerAuBord = (blocs, d) => {
    let x = 0;
    let sens = 1;
    let arrete = false;
    let demiTourAuBord = false;
    const bilan = { rebonds: 0, retoursAuCentre: 0, arretsAuBord: 0 };
    const auBord = () => Math.abs(x) >= BORD;
    const executer = (liste) => {
        for (let i = 0; i < liste.length && !arrete; i++) {
            const b = liste[i];
            if (b.t === 'hat') {
                // le script démarre, rien à faire
            } else if (b.t === 'repeat') {
                for (let k = 0; k < b.n && !arrete; k++) executer(b.body);
            } else if (b.t === 'if') {
                if (b.cond !== 'touche le bord ?') throw new Error(`gen_193 : condition inconnue ${b.cond}`);
                if (auBord()) executer(b.body);
            } else if (b.txt === `avancer de ${d} pas`) {
                const x2 = Math.max(-BORD, Math.min(BORD, x + sens * d));
                if (x2 !== x && demiTourAuBord) {
                    bilan.rebonds++;
                    demiTourAuBord = false;
                }
                x = x2;
            } else if (b.txt === TOURNER_180.txt) {
                sens = -sens;
                demiTourAuBord = auBord();
            } else if (b.txt === ALLER_AU_CENTRE.txt) {
                if (auBord()) bilan.retoursAuCentre++;
                x = 0;
            } else if (b.txt === STOP_TOUT.txt) {
                if (auBord()) bilan.arretsAuBord++;
                arrete = true;
            } else {
                throw new Error(`gen_193 : bloc inconnu ${b.txt}`);
            }
        }
    };
    executer(blocs);
    return bilan;
};

// Figure d'énoncé d'un script seul : le cadre de svgScriptsMemeCadre, dont la
// marge haute garde la bosse du chapeau dans le viewBox.
const svgScriptSeul = (blocs) => svgScriptsMemeCadre([blocs])[0];

// ═════════ Carte « cond_bord » : que fait le lutin au bord ? (QCM) ═════════

// L'action du « si touche le bord ? alors » est tirée, un tiers chacune, parmi
// trois actions de Scratch au comportement certain (décision de revue, seconde
// passe). Les quatre phrases proposées sont les mêmes à chaque tirage : les trois
// comportements, dont un seul est juste, et la sortie de l'écran, jamais juste.
const ACTIONS_BORD = [
    { code: 'tourner_180', bloc: TOURNER_180, compteur: 'rebonds', reponse: 'Il repart en sens inverse : il rebondit sur le bord.' },
    { code: 'stop_tout', bloc: STOP_TOUT, compteur: 'arretsAuBord', reponse: 'Il s\'arrête définitivement au bord.' },
    { code: 'aller_au_centre', bloc: ALLER_AU_CENTRE, compteur: 'retoursAuCentre', reponse: 'Il revient au centre de l\'écran.' },
];
const SORT_DE_L_ECRAN = 'Il sort de l\'écran et disparaît.';

const genCondBord = () => {
    const d = randInt(4, 12) * 5;   // 20 à 60 pas
    const nom = randomChoice(NOMS);
    const action = randomChoice(ACTIONS_BORD);
    const blocs = [DRAPEAU, repeter50([avancerDe(d), siBord([action.bloc])])];
    // Second chemin : le script est exécuté par le lutin au bord ; un seul
    // comportement doit apparaître, celui de la phrase donnée pour juste.
    const bilan = executerAuBord(blocs, d);
    const observes = ACTIONS_BORD.filter((a) => bilan[a.compteur] > 0);
    if (observes.length !== 1 || observes[0] !== action) {
        throw new Error(`gen_193 : le lutin simulé ne fait pas ce que dit la clé (${action.code})`);
    }
    const autres = ACTIONS_BORD.filter((a) => a !== action);
    const codes = [...autres.map((a) => `comme_${a.code}`), 'sort_de_l_ecran'];
    const texte = `<p>${majuscule(nom)}, un lutin, avance de ${d} pas dans une boucle `
        + '« répéter 50 fois ». Voici son script.</p>{{element:0}}'
        + '<p><b>Quand le lutin touche le bord de l\'écran, que fait-il ?</b></p>';
    const payload = makeQcm([action.reponse, ...autres.map((a) => a.reponse), SORT_DE_L_ECRAN], action.reponse, texte,
        { ...opts(L_COND_BORD), elements_additionnels: imgElement(svgScriptSeul(blocs), 'small', altScript(blocs)) });
    payload._meta.params = { d, action: action.code, nom, distracteurs: codes };
    return payload;
};

// ═════════ Carte « cond_script » : quel script fait rebondir ? (QCM images) ═════════

const genCondScript = () => {
    const d = randInt(4, 12) * 5;   // 20 à 60 pas
    const nom = randomChoice(NOMS);
    // Les mêmes cinq blocs dans les quatre scripts, seul l'emboîtement change.
    const scripts = {
        cle: [DRAPEAU, repeter50([avancerDe(d), siBord([TOURNER_180])])],
        si_apres_boucle: [DRAPEAU, repeter50([avancerDe(d)]), siBord([TOURNER_180])],
        si_avant_boucle: [DRAPEAU, siBord([TOURNER_180]), repeter50([avancerDe(d)])],
        si_mal_emboite: [DRAPEAU, repeter50([siBord([avancerDe(d)]), TOURNER_180])],
    };
    const codes = ['si_apres_boucle', 'si_avant_boucle', 'si_mal_emboite'];
    if (executerAuBord(scripts.cle, d).rebonds < 1) throw new Error('gen_193 : la clé ne fait pas rebondir le lutin');
    codes.forEach((c) => {
        if (executerAuBord(scripts[c], d).rebonds !== 0) throw new Error(`gen_193 : ${c} fait aussi rebondir le lutin`);
    });
    const listeBlocs = [scripts.cle, ...codes.map((c) => scripts[c])];
    const choix = svgScriptsMemeCadre(listeBlocs).map((svg, i) => imgChoix(svg, altScript(listeBlocs[i])));
    const texte = `<p>${majuscule(nom)}, un lutin, part du centre de l'écran.</p>`
        + `<p><b>Lequel de ces quatre scripts fait rebondir ${nom} sur les bords de l'écran ?</b></p>`;
    const payload = makeQcm(choix, choix[0], texte, opts(L_COND_SCRIPT));
    payload._meta.params = { d, nom, distracteurs: codes };
    return payload;
};

// ═════════ Carte « phrases » : combien de phrases ? (QCM, À ARBITRER) ═════════

// Deux formes de script, tirées une fois sur deux (seconde passe de revue) :
//   - « sans_phrase_fixe » : le script d'Eduscol, une phrase par tour, clé N ;
//   - « avec_phrase_fixe » : un « dire » sans condition en tête de la boucle,
//     avant le tirage, deux phrases par tour, clé 2N.
// Avec la seule première forme, la clé était toujours la deuxième plus grande
// des quatre valeurs (100 %). Dans la seconde, les trois pièges sont en dessous
// de 2N et la clé est la plus grande ; « chaque bloc dire compté à chaque tour »
// (3N) la remettrait au deuxième rang, elle est écartée (contrat).
const PHRASES = [
    { fixe: 'Je lance la pièce', p0: 'Pile !', p1: 'Face !' },
    { fixe: 'Je choisis un nombre', p0: 'Le nombre choisi est 0', p1: 'Le nombre choisi est 1' },
    { fixe: 'Je joue', p0: 'Gagné !', p1: 'Perdu !' },
    { fixe: 'Voici ma réponse', p0: 'Oui !', p1: 'Non !' },
];

const direPendant2 = (p) => ({ t: 'looks', txt: `dire ${p} pendant 2 secondes` });

const genPhrases = () => {
    const N = randInt(5, 15) * 2;   // 10 à 30, pair : N ÷ 2 est un entier
    const ph = randomChoice(PHRASES);
    const avecFixe = randomChoice([false, true]);
    const corpsBoucle = [
        ...(avecFixe ? [direPendant2(ph.fixe)] : []),
        { t: 'variable', txt: 'mettre tirage à nombre aléatoire entre 0 et 1' },
        { t: 'if', cond: 'tirage = 0', body: [direPendant2(ph.p0)] },
        { t: 'if', cond: 'tirage = 1', body: [direPendant2(ph.p1)] },
    ];
    const blocs = [DRAPEAU, { t: 'repeat', n: N, body: corpsBoucle }];
    // Second chemin : le corps dessiné est exécuté pour chacun des deux tirages
    // possibles ; on compte les blocs « dire » passés, sans condition ou sous une
    // condition vraie. Le compte est le même quel que soit le tirage, la clé est
    // N fois ce compte.
    const ditesParTour = [0, 1].map((tirage) => corpsBoucle.reduce((s, b) => {
        if (b.t === 'looks') return s + 1;
        if (b.t === 'if' && Number(b.cond.split(' = ')[1]) === tirage) return s + b.body.filter((x) => x.t === 'looks').length;
        return s;
    }, 0));
    if (ditesParTour[0] !== ditesParTour[1]) throw new Error('gen_193 : le nombre de phrases dépend du tirage');
    const cle = N * ditesParTour[0];
    if (cle !== (avecFixe ? 2 * N : N)) throw new Error(`gen_193 : ${cle} phrases, clé inattendue`);
    const pieges = avecFixe
        ? { phrase_sans_condition_oubliee: N, moitie: N + N / 2, une_phrase_par_bloc_dire: 3 }
        : { deux_si_comptes: 2 * N, moitie: N / 2, une_phrase_par_si: 2 };
    const codes = Object.keys(pieges);
    const valeurs = [cle, ...codes.map((c) => pieges[c])];
    if (new Set(valeurs).size !== 4 || !valeurs.every(Number.isInteger)) {
        throw new Error('gen_193 : quatre valeurs entières distinctes attendues');
    }
    const texte = '<p>On clique une fois sur le drapeau vert. Le lutin exécute ce script.</p>{{element:0}}'
        + '<p><b>Au total, combien de fois le lutin dit-il une phrase ?</b></p>';
    const payload = makeQcm(valeurs.map(String), String(cle), texte,
        { ...opts(L_PHRASES), elements_additionnels: imgElement(svgScriptSeul(blocs), 'medium', altScript(blocs)) });
    payload._meta.params = {
        N,
        forme: avecFixe ? 'avec_phrase_fixe' : 'sans_phrase_fixe',
        phrases: avecFixe ? [ph.fixe, ph.p0, ph.p1] : [ph.p0, ph.p1],
        distracteurs: codes,
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    poly_regulier_texte_trou_drag: genPolyRegulier,
    poly_rectangle_texte_trou_saisie: genPolyRectangle,
    rect_couple_qcm_unique: genRectCouple,
    cond_bord_qcm_unique: genCondBord,
    cond_script_qcm_unique: genCondScript,
    phrases_qcm_unique: genPhrases,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag', qcm_unique: 'QCM' };
const QCM_IMAGES = new Set(['rect_couple_qcm_unique', 'cond_script_qcm_unique']);

const CARTES = [
    { id: 'poly_regulier', label: L_POLY },
    { id: 'poly_rectangle', label: L_RECT },
    { id: 'rect_couple', label: L_RECT_COUPLE },
    { id: 'cond_bord', label: L_COND_BORD },
    { id: 'cond_script', label: L_COND_SCRIPT },
    { id: 'phrases', label: L_PHRASES },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: QCM_IMAGES.has(k) ? 'QCM images' : (FORMAT_LABELS[k.slice(c.id.length + 1)] || k) })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
